import React, { Dispatch, SetStateAction, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Box,
  Typography,
  Divider,
  Stack,
  Chip,
  Paper,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import { AccountBalance, ScreenSearchDesktop, Business } from "@mui/icons-material";
import { Controller } from "react-hook-form";
import axiosInstance from "utilities/axios";
import PermissionsCheckboxGroup, { Permission } from "./PermissionsCheckboxGroup";
import useDialog from "hooks/useDialog";
import { getUserTab, isTabDisabled } from "utils/userFormatting";
import { useAuth } from "context/AuthContext";
import { useUserForm } from "hooks/useUserForm";

type AgencyDepartment = { _id: string; title: string };
type MonitoringOrg    = { _id: string; name: string; org_type?: string };

type Props = {
  open:              boolean;
  setOpen:           Dispatch<SetStateAction<boolean>>;
  agencyDepartments: AgencyDepartment[];
  isMonitoring:      boolean;
  editUser?:         any;
  onUserUpdated?:    () => void;
};

const AGENCY_PERMISSIONS: Permission[] = [
  { name: "მომხმარებლების მართვა",    value: "USER_MANAGE" },
  { name: "კომპანიების მართვა",       value: "MANAGE_MONITORING_COMPANIES" },
  { name: "ფორმების შევსება",         value: "SUBMIT_MONITORING_FORMS" },
  { name: "მონიტორინგის განხილვა",   value: "REVIEW_MONITORING" },
  { name: "განაცხადის გადამისამართება", value: "REASSIGN_APPLICATIONS" },
  { name: "გადახდების მართვა",        value: "MANAGE_PAYMENTS" },
  { name: "განაცხადის დამატება",      value: "MANAGE_MONITORING_AGREEMENTS" },
];

const MONITORING_PERMISSIONS: Permission[] = [
  { name: "მომხმარებლების მართვა",    value: "USER_MANAGE" },
  { name: "ფორმების შევსება",         value: "SUBMIT_MONITORING_FORMS" },
  { name: "მონიტორინგის განხილვა",   value: "REVIEW_MONITORING" },
];

const ORG_ROLE_LABELS: Record<string, string> = {
  member:   "თანამშრომელი",
  head:     "დეპ. უფროსი",
};

const MONITORING_ROLE_LABELS: Record<string, string> = {
  member: "სპეციალისტი",
  head:   "ხელმძღვანელი",
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.8}>
      {children}
    </Typography>
  );
}

export default function UserCreateDialog({ open, setOpen, agencyDepartments = [], isMonitoring, editUser, onUserUpdated }: Props) {
  const isEditMode              = !!editUser;
  const { showSnackbar }        = useDialog();
  const { user: currentUser }   = useAuth();
  const myOrg                   = isMonitoring ? currentUser?.organization : null;

  const [monitoringOrgs, setMonitoringOrgs] = React.useState<MonitoringOrg[]>([]);
  const [loading, setLoading]               = React.useState(false);

  const form = useUserForm();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = form;
  const userType = watch("user_type");
  const roleLabels  = userType === "monitoring" ? MONITORING_ROLE_LABELS : ORG_ROLE_LABELS;
  const roleOptions = ["member", "head"];

  useEffect(() => {
    if (open) {
      axiosInstance.get("/organizations", { params: { org_type: "monitoring_company" } })
        .then((res) => setMonitoringOrgs(res.data))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (isEditMode && editUser && open) {
      const parts = editUser.name?.split(" ") || ["", ""];
      reset({
        firstName:    parts[0] || "",
        lastName:     parts.slice(1).join(" ") || "",
        email:        editUser.email || "",
        user_type:    getUserTab(editUser.user_type) as any,
        permissions:  editUser.permissions || [],
        org_role:     editUser.org_role || "member",
        organization: editUser.organization?._id || editUser.organization || "",
        department:   editUser.department?._id || editUser.department || "",
      });
    } else if (!isEditMode && open) {
      const myOrgId = isMonitoring ? (currentUser?.organization?._id || "") : "";
      reset({
        firstName: "", lastName: "", email: "",
        user_type: isMonitoring ? "monitoring" : "agency",
        org_role: "member",
        organization: myOrgId,
        department: "",
        permissions: [],
      });
    }
  }, [isEditMode, editUser, open]);

  function onSubmit(values: ReturnType<typeof form.getValues>) {
    setLoading(true);
    const payload = {
      name:         `${values.firstName} ${values.lastName}`.trim(),
      email:        values.email,
      user_type:    values.user_type,
      org_role:     values.org_role,
      organization: values.organization || null,
      department:   values.user_type === "agency" ? (values.department || null) : null,
      permissions:  values.permissions,
    };
    const req = isEditMode
      ? axiosInstance.put(`/auth/user/${editUser._id}`, payload)
      : axiosInstance.post("/auth/signup", payload);
    req
      .then((res) => { showSnackbar(res.data.message, "success"); if (onUserUpdated) onUserUpdated(); setOpen(false); })
      .catch((err) => showSnackbar(err.response?.data?.message || "შეცდომა", "error"))
      .finally(() => setLoading(false));
  }

  function handleDeactivation() {
    axiosInstance.post("/auth/deactivation", { userId: editUser._id, deactivate: !editUser.deactivated })
      .then((res) => { showSnackbar(res.data.message, "success"); if (onUserUpdated) onUserUpdated(); setOpen(false); })
      .catch((err) => showSnackbar(err.response?.data?.message || "შეცდომა", "error"));
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="user_type"
          control={control}
          render={({ field }) => (
            <TabContext value={field.value}>
              {/* Tab selector — agency admins only */}
              {!isMonitoring && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
                  <Tabs
                    value={field.value}
                    onChange={(_, v) => { if (!isEditMode) { field.onChange(v); setValue("permissions", []); } }}
                    variant="fullWidth"
                  >
                    <Tab
                      icon={<AccountBalance fontSize="small" />}
                      iconPosition="start"
                      label="სააგენტო"
                      value="agency"
                      disabled={isTabDisabled("agency", field.value, isEditMode, isMonitoring)}
                      sx={{ minHeight: 48 }}
                    />
                    <Tab
                      icon={<ScreenSearchDesktop fontSize="small" />}
                      iconPosition="start"
                      label="მონიტ. კომპანია"
                      value="monitoring"
                      disabled={isTabDisabled("monitoring", field.value, isEditMode, isMonitoring)}
                      sx={{ minHeight: 48 }}
                    />
                  </Tabs>
                </Box>
              )}

              <DialogTitle sx={{ pb: 0 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6">
                    {isEditMode ? "მომხმარებლის რედაქტირება" : "ახალი მომხმარებელი"}
                  </Typography>
                  <Chip
                    size="small"
                    label={field.value === "agency" ? "სააგენტო" : "მონიტ. კომპანია"}
                    color={field.value === "agency" ? "primary" : "secondary"}
                    variant="outlined"
                  />
                </Box>
              </DialogTitle>

              <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={3}>
                  {/* Name & Email */}
                  <Box>
                    <SectionLabel>საბაზისო ინფორმაცია</SectionLabel>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Box display="flex" gap={2}>
                        <Controller
                          name="firstName"
                          control={control}
                          rules={{ required: "სავალდებულო" }}
                          render={({ field: f, fieldState }) => (
                            <TextField {...f} size="small" fullWidth label="სახელი" error={!!fieldState.error} helperText={fieldState.error?.message} />
                          )}
                        />
                        <Controller
                          name="lastName"
                          control={control}
                          rules={{ required: "სავალდებულო" }}
                          render={({ field: f, fieldState }) => (
                            <TextField {...f} size="small" fullWidth label="გვარი" error={!!fieldState.error} helperText={fieldState.error?.message} />
                          )}
                        />
                      </Box>
                      <Controller
                        name="email"
                        control={control}
                        rules={{ required: "სავალდებულო", pattern: { value: /^\S+@\S+\.\S+$/, message: "არასწორი ფორმატი" } }}
                        render={({ field: f, fieldState }) => (
                          <TextField {...f} size="small" fullWidth label="ელ-ფოსტა" type="email" error={!!fieldState.error} helperText={fieldState.error?.message} />
                        )}
                      />
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Agency tab */}
                  <TabPanel value="agency" sx={{ p: 0 }}>
                    <Stack spacing={2}>
                      <Box>
                        <SectionLabel>პოზიცია და განყოფილება</SectionLabel>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                          <Controller
                            name="org_role"
                            control={control}
                            render={({ field: f }) => (
                              <Box>
                                <Typography variant="body2" color="text.secondary" mb={0.5}>პოზიცია</Typography>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                  {roleOptions.map((role) => (
                                    <Chip
                                      key={role}
                                      label={roleLabels[role]}
                                      onClick={() => f.onChange(role)}
                                      color={f.value === role ? "primary" : "default"}
                                      variant={f.value === role ? "filled" : "outlined"}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          />
                          <Controller
                            name="department"
                            control={control}
                            render={({ field: f }) => (
                              <FormControl size="small" fullWidth>
                                <InputLabel>დეპარტამენტი</InputLabel>
                                <Select {...f} label="დეპარტამენტი">
                                  <MenuItem value=""><em>—</em></MenuItem>
                                  {agencyDepartments.map((d) => <MenuItem key={d._id} value={d._id}>{d.title}</MenuItem>)}
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Stack>
                      </Box>
                      <Divider />
                      <Box>
                        <SectionLabel>უფლებები</SectionLabel>
                        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 1 }}>
                          <Controller
                            name="permissions"
                            control={control}
                            render={({ field: f }) => (
                              <PermissionsCheckboxGroup
                                permissions={AGENCY_PERMISSIONS}
                                userPermissions={f.value}
                                setUserPermissions={f.onChange}
                              />
                            )}
                          />
                        </Paper>
                      </Box>
                    </Stack>
                  </TabPanel>

                  {/* Monitoring tab */}
                  <TabPanel value="monitoring" sx={{ p: 0 }}>
                    <Stack spacing={2}>
                      <Box>
                        <SectionLabel>კომპანია და პოზიცია</SectionLabel>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                          {isMonitoring && myOrg ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "grey.50" }}>
                              <Business fontSize="small" sx={{ color: "text.secondary" }} />
                              <Typography variant="body2">{(myOrg as any).name || "კომპანია"}</Typography>
                            </Box>
                          ) : (
                            <Controller
                              name="organization"
                              control={control}
                              render={({ field: f }) => (
                                <FormControl size="small" fullWidth>
                                  <InputLabel>კომპანია</InputLabel>
                                  <Select
                                    {...f}
                                    label="კომპანია"
                                    startAdornment={f.value ? <Business fontSize="small" sx={{ ml: 1, mr: 0.5, color: "text.secondary" }} /> : null}
                                  >
                                    <MenuItem value=""><em>— კომპანია —</em></MenuItem>
                                    {monitoringOrgs.map((o) => (
                                      <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          )}
                          <Controller
                            name="org_role"
                            control={control}
                            render={({ field: f }) => (
                              <Box>
                                <Typography variant="body2" color="text.secondary" mb={0.5}>პოზიცია</Typography>
                                <Box display="flex" gap={1}>
                                  {roleOptions.map((role) => (
                                    <Chip
                                      key={role}
                                      label={roleLabels[role]}
                                      onClick={() => f.onChange(role)}
                                      color={f.value === role ? "secondary" : "default"}
                                      variant={f.value === role ? "filled" : "outlined"}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          />
                        </Stack>
                      </Box>
                      <Divider />
                      <Box>
                        <SectionLabel>უფლებები</SectionLabel>
                        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 1 }}>
                          <Controller
                            name="permissions"
                            control={control}
                            render={({ field: f }) => (
                              <PermissionsCheckboxGroup
                                permissions={MONITORING_PERMISSIONS}
                                userPermissions={f.value}
                                setUserPermissions={f.onChange}
                              />
                            )}
                          />
                        </Paper>
                      </Box>
                    </Stack>
                  </TabPanel>
                </Stack>
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
                <Button onClick={() => setOpen(false)} color="inherit">გაუქმება</Button>
                {isEditMode && (
                  <Button
                    onClick={handleDeactivation}
                    color={editUser?.deactivated ? "success" : "error"}
                    variant="outlined"
                    sx={{ mr: "auto" }}
                  >
                    {editUser?.deactivated ? "აქტივაცია" : "დეაქტივაცია"}
                  </Button>
                )}
                <Button type="submit" variant="contained" disabled={loading}>
                  {isEditMode ? "შენახვა" : "დამატება"}
                </Button>
              </DialogActions>
            </TabContext>
          )}
        />
      </form>
    </Dialog>
  );
}
